'use client';

import dynamic from 'next/dynamic';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsAddToListButtonTapped from '@alltrails/amplitude/events/logTrailDetailsAddToListButtonTapped';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import Button from '@alltrails/denali/components/Button';
import Heart from '@alltrails/denali/icons/Heart';
import HeartFilled from '@alltrails/denali/icons/HeartFilled';
import { useExposureEvent } from '@alltrails/experiments';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import { DirectionsCta, TrailPage } from '@alltrails/trails';
import type TrailDetails from '@alltrails/types/TrailDetails';
import SaveSuccessToast from '@/components/SaveSuccessToast/SaveSuccessToast';
import useSaveSuccessToast from '@/components/SaveSuccessToast/useSaveSuccessToast';
import getTrailPageType from '@/utils/getTrailPageType';
import styles from './DesktopCtas.module.scss';

const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });

const reduxListsAnalyticsInfo = { detailedCardLocation: 'trail banner' as const, page: PageStrings.TRAIL_PAGE };

type DesktopCtasProps = {
  trailDetails: TrailDetails;
  trailPage: TrailPage;
};

const DesktopCtas = ({ trailDetails, trailPage }: DesktopCtasProps) => {
  const { id, location } = trailDetails;
  const [isSendToPhoneModalOpen, setIsSendToPhoneModalOpen] = useState(false);
  const sendSaveToastExposure = useExposureEvent('web-growth-rm-save-toast');
  const { isOpen, onSaveToListModalClose, savedLists, setIsOpen } = useSaveSuccessToast();

  const { isFavorite, handleFavoriteClick, listModal, signUpModal } = useReduxListItems(
    undefined,
    false /* preventAutoInitialize */,
    reduxListsAnalyticsInfo,
    onSaveToListModalClose
  );

  const isTrailFavorite = isFavorite('trail', id);

  const trailPageType = getTrailPageType(trailDetails);

  const onFavoriteClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: id.toString(),
      trail_page_action: 'save',
      trail_page_location: 'bottom floating bar light',
      trail_page_type: trailPageType
    });
    logTrailDetailsAddToListButtonTapped({ trail_id: id });
    sendSaveToastExposure();

    handleFavoriteClick('trail', id);
  }, [handleFavoriteClick, id, sendSaveToastExposure, trailPageType]);

  return (
    <div className={styles.desktopCtasContainer}>
      <div className={styles.floating} role="region">
        <Button
          icon={isTrailFavorite ? <HeartFilled /> : <Heart />}
          onClick={onFavoriteClick}
          size="md"
          text={isTrailFavorite ? <FormattedMessage defaultMessage="Saved" /> : <FormattedMessage defaultMessage="Save" />}
          variant={isTrailFavorite ? 'default' : 'accent'}
        />

        <DirectionsCta
          buttonLocation="bottom floating bar light"
          location={location}
          trailId={id}
          size="md"
          displayAsButton
          trailPageType={trailPageType}
        />

        <Button
          size="md"
          text={<FormattedMessage defaultMessage="Send to phone" />}
          variant="default"
          onClick={() => setIsSendToPhoneModalOpen(true)}
        />
      </div>

      {listModal}
      {signUpModal}
      <SaveSuccessToast isOpen={isOpen} savedLists={savedLists} setIsOpen={setIsOpen} />

      {isSendToPhoneModalOpen && (
        <HitTheTrailModal
          includeOpenInApp
          includeDownloadInApp
          includeSendToGarmin={false}
          includeExportMapFile={false}
          isOpen={isSendToPhoneModalOpen}
          onCloseRequest={() => setIsSendToPhoneModalOpen(false)}
          steps={['initial']}
          title={<FormattedMessage defaultMessage="Send to phone" />}
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}
    </div>
  );
};

export default DesktopCtas;
